import { BaseSyntheticEvent, useRef } from 'react';

type TSubmitHandler = (event?: BaseSyntheticEvent) => Promise<void>;

/**
 * Hook that guards a form submit handler against repeated submissions.
 * While a submission is in flight, further submits (repeated clicks on a submit button or
 * pressing Enter in a field) are prevented instead of running the handler again.
 * Use it to wrap react-hook-form's `handleSubmit(...)`: a loading `Button` doesn't stop a
 * native form submission, and a second `handleSubmit` call would reset `isSubmitting` while
 * the first submission is still in flight.
 * @param handler - The submit handler to guard, e.g. `methods.handleSubmit(onSubmit, onError)`.
 * @returns The guarded submit handler to pass to the form's `onSubmit`.
 */
export const useSubmitGuard = (handler: TSubmitHandler) => {
  const isSubmittingRef = useRef(false);

  return async (event?: BaseSyntheticEvent) => {
    if (isSubmittingRef.current) {
      event?.preventDefault();
      return;
    }
    isSubmittingRef.current = true;
    try {
      await handler(event);
    } finally {
      isSubmittingRef.current = false;
    }
  };
};
